import { fileParser } from "@senciamatch/frontend/libs/csv";
import { staffShiftTableParser } from "@senciamatch/frontend/libs/csvParser/staffsShiftTable";
import { getJapaneseDayOfWeek } from "@senciamatch/frontend/libs/getJapaneseDayOfWeek";
import { trpc } from "@senciamatch/frontend/libs/trpc";
import type { StaffShiftTable } from "@senciamatch/shared/models/staffShiftTable";
import type React from "react";
import { type DragEvent, useCallback, useState } from "react";
import { v7 as uuidv7 } from "uuid";

interface Props {
  setStaffShiftGroupId: (id: string) => void;
  setCurrentPage: (page: "buildings" | "staffs") => void;
}

export const MatchingStaffs: React.FC<Props> = ({ setStaffShiftGroupId, setCurrentPage }) => {
  const [staffShiftTable, setStaffShiftTable] = useState<StaffShiftTable>();
  const [errors, setErrors] = useState<string[]>([]);
  const getStaffs = trpc.getStaffs.useQuery();
  const staffs = getStaffs.data?.staffs ?? [];

  const createStaffShiftGroupMutation = trpc.createStaffShiftGroup.useMutation();

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (!file) return;
      try {
        const data = await fileParser(file);

        if (!Array.isArray(data)) return;

        const parsedStaffs = await staffShiftTableParser.parse(data, staffs);

        setStaffShiftTable(parsedStaffs.data);
        setErrors(parsedStaffs.errors);
      } catch (error) {
        console.error(error);
      }
    },
    [staffs],
  );

  const handleSubmit = useCallback(async () => {
    if (!staffShiftTable) return;
    if (createStaffShiftGroupMutation.isPending) return;

    const params = {
      staffShiftGroup: staffShiftTable.staffShiftGroup,
      staffShifts: staffShiftTable.rows.flatMap((row) => row.cells).filter((cell) => cell !== null),
    };

    await createStaffShiftGroupMutation.mutateAsync(params);

    setStaffShiftGroupId(staffShiftTable.staffShiftGroup.id);
    setCurrentPage("buildings");
  }, [staffShiftTable, createStaffShiftGroupMutation, setCurrentPage, setStaffShiftGroupId]);

  return (
    <div className="h-screen flex flex-col">
      <h2 className="text-2xl font-bold py-10 flex-shrink-0">スタッフのシフト希望登録</h2>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="rounded-md border-dashed border-2 border-gray-200 bg-white p-4 min-h-40 flex flex-col justify-center items-center mb-10"
      >
        <p className="text-center">ファイルをここにドラッグ＆ドロップしてください</p>
      </div>

      {staffShiftTable?.rows && (
        <div className="flex flex-col h-full">
          {createStaffShiftGroupMutation.error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              <p className="font-bold">エラーが発生しました</p>
              <p className="text-sm">スタッフシフトグループの作成に失敗しました。再度お試しください。</p>
            </div>
          )}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              <p className="font-bold">入力エラーがあります</p>
              <ul className="list-disc list-inside">
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex-grow overflow-hidden border bg-white border-base-100  flex flex-col">
            <div className="flex items-center justify-between">
              <p className="text-xl font-bold ml-8 mt-8">スタッフのシフト情報</p>
              <button
                type="button"
                className="bg-primary-700 text-white px-4 py-2 rounded-md mt-8 mx-8 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSubmit}
                disabled={createStaffShiftGroupMutation.isPending}
              >
                {createStaffShiftGroupMutation.isPending ? "登録中..." : "新規登録"}
              </button>
            </div>

            {/* <p className="text-red-500 mx-8">1件の入力エラーがあります。</p> */}

            <div className="h-full overflow-auto ml-8 mt-8">
              <table className="w-[2450px] border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="sticky top-0 left-0 bg-gray-100 p-2 border border-gray-100 z-20 min-w-[300px]">
                      スタッフ
                    </th>
                    {Array.from({ length: staffShiftTable.rows[0]?.cells?.length ?? 0 }, (_, i) => {
                      const dayOfWeek = getJapaneseDayOfWeek(
                        staffShiftTable.staffShiftGroup.year,
                        staffShiftTable.staffShiftGroup.month,
                        i + 1,
                      );
                      const bgColor =
                        dayOfWeek === "日" ? "bg-red-100" : dayOfWeek === "土" ? "bg-blue-100" : "bg-gray-100";
                      return (
                        <th key={uuidv7()} className={`sticky top-0 ${bgColor} p-2 border border-gray-100 z-10`}>
                          {i + 1}
                          <br />
                          {dayOfWeek}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {staffShiftTable.rows.map((row) => {
                    const hasError = row.cells.some(
                      (s) => s != null && !staffShiftTableParser.VALID_SHIFT.includes(s.shiftSymbol),
                    );
                    return (
                      <tr key={uuidv7()}>
                        <td
                          className={`sticky left-0 p-2 bg-white border border-gray-100 z-10 ${hasError ? "bg-red-300" : ""}`}
                        >
                          {row.label}
                        </td>
                        {row.cells.map((cell) => {
                          return (
                            <td
                              key={uuidv7()}
                              className={`border border-gray-100 w-[270px] h-[30px] bg-white text-center ${
                                cell != null && staffShiftTableParser.VALID_SHIFT.includes(cell.shiftSymbol)
                                  ? ""
                                  : "bg-red-300"
                              }`}
                            >
                              {cell != null ? <span>{cell.shiftSymbol}</span> : null}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
